import { KalturaAsset, KalturaAssetType } from '@kaltura-ott/ott-client-types/api/types';
import {
  AdditionalMediaType,
  assetToType,
  ExtendedMediaType,
  getSeasonsList,
  getSeriesListV2,
  KuxList,
  MediaType,
  PresentationAdditionalTypeEnum,
  Season,
} from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';
import { Subject, lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { assertPresence } from '../../../utils/error-handling';
import { CardPlaceholder, Episode, Program, SeriesItemType } from '../../entities';
import {
  addBookmarkToPlayable,
  addBookmarkToRecords,
  addPurchasableAttribute,
  addPurchaseStatusToPlayable,
  addRentPossibilitiesToPlayable,
  addSubscriptionPossibilitiesToPlayable,
  creatorEntityStore,
} from '../../entities/entities.service';
import type { RecordRepository } from '../../recordRepository';
import { RailStoreV2 } from '../widgets/rails';
import { getItemsInPage, DEFAULT_PAGE_SIZE } from '../widgets/rails/rail-store-helper-v2';

/**
 * Details page store for series
 * @category Store
 */
export class SeriesAdditionalRailStoreV2 extends RailStoreV2<SeriesItemType> {
  subtype: PresentationAdditionalTypeEnum.series_additional_rail =
    PresentationAdditionalTypeEnum.series_additional_rail;
  @observable seasonsList: Season[] = [];
  @observable seasonNumber = 0;
  @observable firstEpisode?: SeriesItemType;
  // Indicate the amount of episodes in cases that the series has no seasons.
  @observable totalNumberOfEpisodes?: number = undefined;

  private startSeasonNumber = 0;
  private seriesId = '';
  private assetType?: ExtendedMediaType;
  private episodesCancelSubject = new Subject();

  private purchasableAttribute?: boolean;

  @computed get seasonsCount() {
    return this.seasonsList?.length || 0;
  }

  public async loadSeasonList(seriesId?: string, assetType?: KalturaAssetType | string) {
    if (seriesId) {
      this.seasonsList = await lastValueFrom(getSeasonsList(seriesId, assetType));
      this.startSeasonNumber = Number(this.seasonsList[0]?.seasonNumber) || 0;
      this.seriesId = seriesId;
    }
  }

  public async setupSeasonNumber(seasonNumber?: number) {
    this.resetStore();

    if (seasonNumber) {
      this.episodesCancelSubject.next(null);
      this.episodesCancelSubject.complete();
      this.episodesCancelSubject = new Subject();
      this.seasonNumber = seasonNumber;
    } else {
      this.seasonNumber = this.startSeasonNumber;
    }

    await this.init();
  }

  public async setupAssetType(type: MediaType | AdditionalMediaType) {
    this.assetType = type;
  }

  protected async getData(
    pageIndex: number,
    items: (SeriesItemType | CardPlaceholder)[],
  ): Promise<{ items: (SeriesItemType | CardPlaceholder)[]; totalCount: number; newItems: SeriesItemType[] }> {
    const assetType = assertPresence(this.assetType);
    const recordRepository = container.resolve<RecordRepository>('recordRepository');
    const newRailData = await new Promise<KuxList<KalturaAsset>>((resolve, reject) => {
      getSeriesListV2({
        seasonNumber: this.seasonNumber,
        seriesId: this.seriesId,
        pageSize: DEFAULT_PAGE_SIZE,
        pageIndex: pageIndex,
        assetType: assetType,
        isRecords: this.assetType === AdditionalMediaType.Recording,
        cancelSubject: this.episodesCancelSubject,
      }).subscribe(
        (newRailData) => {
          resolve(newRailData as KuxList<KalturaAsset>);
        },
        reject,
        () => null,
      );
    });

    if (!newRailData) {
      return { items: this.items, totalCount: 0, newItems: [] };
    }

    const newItems = newRailData.items.map((asset) => {
      const program = assetToType(asset, {
        newEpisodeBadgeDays: this.newEpisodeBadgeDays,
        recordingExpirationBoundary: this.recordingExpirationBoundary,
      });
      if (program) {
        const entity = creatorEntityStore(program) as SeriesItemType;
        // @ts-expect-error TODO Fix
        if (entity instanceof Program && !entity.record && entity.data.recordData.recordingId) {
          recordRepository.addSingleRecordToRepository(entity.id, {
            // @ts-expect-error TODO Fix
            id: entity.data.recordData.recordingId,
            // @ts-expect-error TODO Fix
            type: entity.data.recordData.recordingType,
            // @ts-expect-error TODO Fix
            viewableUntilDate: entity.data.recordData.viewableUntilDate,
            expiredInDays: entity.recordingExpirationInDays,
            // @ts-expect-error TODO Fix
            totalRecordedEpisodes: entity.data.recordData.totalRecordedEpisodes,
          });
        }
        return entity;
      }
    }) as SeriesItemType[];

    const filteredItems: { [type in number]: SeriesItemType } = {};

    newItems.forEach((item) => {
      if (!item.data.episodeNumber) {
        filteredItems[item.id] = item;
        return;
      }
      filteredItems[item.data.episodeNumber] = item;
    });

    const filteredItemsArray = Object.values(filteredItems);

    this.innerItems.push(...filteredItemsArray);

    if (!this.seasonsCount) {
      this.totalNumberOfEpisodes = newRailData.totalCount;
    }
    return { items: this.items, totalCount: newRailData.totalCount, newItems: filteredItemsArray };
  }

  protected async getAdditionData(itemsInPage: SeriesItemType[]): Promise<void> {
    const bookmarkAssetType = this.assetType === MediaType.Episode ? KalturaAssetType.media : KalturaAssetType.epg;

    const promises = [];
    if (this.assetType === AdditionalMediaType.Recording) {
      promises.push(addBookmarkToRecords(itemsInPage as Program[]));
    } else {
      promises.push(addBookmarkToPlayable(itemsInPage, bookmarkAssetType));
    }
    await Promise.all([promises]);

    if (!this.firstEpisode && this.items[0]) {
      const firstEpisode = this.items[0] as SeriesItemType;

      await addPurchaseStatusToPlayable([firstEpisode]);
      this.purchasableAttribute = !firstEpisode.isPurchased;
      if (!firstEpisode.isPurchased) {
        await addSubscriptionPossibilitiesToPlayable(firstEpisode, firstEpisode.price);

        if (firstEpisode instanceof Episode) {
          await addRentPossibilitiesToPlayable([firstEpisode]);
        }
      }

      this.firstEpisode = firstEpisode;
    }

    // All episodes have same status (firstEpisode status)
    if (this.purchasableAttribute !== undefined) {
      addPurchasableAttribute(itemsInPage, this.purchasableAttribute);
    }

    return;
  }

  protected async restoreAdditionData(pageIndex: number, items: SeriesItemType[]): Promise<void> {
    return this.getAdditionData(getItemsInPage(items, pageIndex));
  }

  protected getRestoreDataKsql(): string | undefined {
    if (this.assetType === AdditionalMediaType.Recording) {
      return "(and asset_type='recording')";
    }
    return undefined;
  }
}
